const AbstractItemRepository = require('@/lib/Items/AbstractItemRepository');
const DataStoreNames = require('@/lib/DataStores/DataStoreNames');
const Ticket = require('./Ticket');
const { createModelBasicHydrator } = require('@/lib/Hydration/basicHydrator');

class TicketRepository extends AbstractItemRepository {
    /**
     * @param {DataStore} dataStore
     * @param {Hydrator} hydrator
     * @param {SyncStateManager} syncStateManager
     * @param {ItemBarcodeRepository} itemBarcodeRepository
     * @param {SessionEventRepository} sessionEventRepository
     * @param ticketResource
     * @param {UserService} userService
     * @param {WaiversService} waiversService
     */
    constructor(
        dataStore,
        hydrator,
        syncStateManager,
        itemBarcodeRepository,
        sessionEventRepository,
        ticketResource,
        userService,
        waiversService
    ) {
        super(dataStore, hydrator, syncStateManager, itemBarcodeRepository);

        this.sessionEventRepository = sessionEventRepository;
        this.ticketResource = ticketResource;
        this.userService = userService;
        this.waiversService = waiversService;
    }

    getEntityClass() {
        return Ticket;
    }

    getEntityName() {
        return 'Ticket';
    }

    getTableName() {
        return DataStoreNames.TICKETS;
    }

    getResource() {
        return this.ticketResource;
    }

    /**
     * @param {number|string} id
     * @return {Promise<Ticket>}
     */
    async find(id) {
        /**
         * @type {Ticket}
         */
        const ticket = await super.find(id);
        await this.sessionEventRepository.attachEventsToTickets([ticket]);
        return ticket;
    }

    /**
     * @param {number} catID
     *
     * @return {Promise<Ticket[]>}
     */
    async findByCategory(catID) {
        /**
         * @type {Ticket[]}
         */
        const tickets = await super.findByCategory(catID);
        await this.sessionEventRepository.attachEventsToTickets(tickets);
        return tickets;
    }

    /**
     * @param {number} branchID
     * @param {number} eventID
     *
     * @return {Promise<Ticket[]>}
     */
    findByEvent(branchID, eventID) {
        return this.dataStore
            .getStore(this.getTableName())
            .where({ branchIDs: branchID })
            .and((ticketData) => {
                if (ticketData.status !== 'Active') {
                    return false;
                }

                if (ticketData.event) {
                    return ticketData.event.ID === eventID;
                }

                return ticketData.eventCatID === eventID;
            })
            .toArray()
            .then(createModelBasicHydrator(this.getEntityClass()))
            .then((tickets) => this.sessionEventRepository.attachEventsToTickets(tickets));
    }

    /**
     * @param {number} branchID
     *
     * @return {Promise<Ticket[]>}
     */
    async findActiveForBranch(branchID) {
        /**
         * @type {Ticket[]}
         */
        const tickets = await super.findActiveForBranch(branchID);
        await this.sessionEventRepository.attachEventsToTickets(tickets);
        return tickets;
    }

    /**
     * @param {number} branchID
     *
     * @return {Promise}
     */
    async sync(branchID) {
        await super.sync(branchID);

        // If the company has waivers enabled, set the `hasWaivers` property on tickets.
        if (this.userService.getCompany().waiversEnabled()) {
            try {
                await this.waiversService.attachWaiverStatusToTickets(this);
            } catch (e) {
                // It's not the end of the world if we can't load the waiver status for tickets.
                // This is currently only used to show an icon on the ticket buttons, and to
                // prevent selling waiver tickets while offline.
                console.warn('Failed to load waiver status for tickets.', e);
            }
        }
    }
}

module.exports = TicketRepository;
